/**
 * LearnerWelcomeBanner — welcome card with the mascot + greeting.
 *
 * Two visual modes (Figma 9174:279063 desktop, 9179:279119 mobile):
 *   - Mobile (< lg): light pastel peach → lavender gradient, dark text,
 *     two-line greeting ("Welcome back" / full name).
 *   - Desktop (≥ lg): dark purple radial gradient, white text, single-line
 *     greeting ("Welcome Back, {firstName}!").
 *
 * Mascot lives at /public/learner/welcome-mascot.png.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';

interface LearnerWelcomeBannerProps {
  firstName: string;
  /** Optional full name for the mobile two-line greeting. Falls back to firstName. */
  fullName?: string;
}

const LearnerWelcomeBanner: React.FC<LearnerWelcomeBannerProps> = ({
  firstName,
  fullName,
}) => {
  const reduceMotion = useReducedMotion();
  const displayName = fullName?.trim() || firstName;

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      className="
        relative overflow-visible rounded-[20px]
        min-h-[120px] sm:min-h-[150px] lg:min-h-[190px]
        bg-[linear-gradient(110deg,rgb(var(--color-secondary-100))_0%,rgb(var(--color-primary-50))_55%,rgb(var(--color-primary-100))_100%)]
        lg:bg-[radial-gradient(circle_at_70%_50%,rgb(var(--color-primary-400))_0%,rgb(var(--color-primary-600))_45%,rgb(var(--color-primary-950))_100%)]
      "
    >
      {/* Desktop-only vignette darkens the corners against the deep purple.
          Rounded to match the card — the container no longer clips. */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute inset-0 rounded-[20px]"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, transparent 55%, rgba(0,0,0,0.28) 100%)',
        }}
      />

      {/* Desktop mascot — anchored to the CARD (direct child, like the
          trainer banner), ~9% taller than it, so the rectangle's top edge
          ends at Tery's body and the tail dome pokes out past it. */}
      <img
        src="/learner/welcome-mascot.png"
        alt=""
        draggable={false}
        className="
          hidden lg:block absolute bottom-0 left-8 h-[206px] w-auto
          select-none pointer-events-none
          drop-shadow-[0_8px_18px_rgba(0,0,0,0.3)]
        "
      />

      <div className="relative flex items-end lg:items-center min-h-[inherit] px-4 sm:px-6 lg:px-8 lg:pl-[280px] py-3 lg:py-4 gap-3 sm:gap-4 lg:gap-5">
        {/* Mobile mascot — in-flow next to the greeting, small peek over
            the top edge (visible now that the card doesn't clip). */}
        <img
          src="/learner/welcome-mascot.png"
          alt=""
          draggable={false}
          className="
            lg:hidden shrink-0 self-end select-none
            w-24 sm:w-28 h-auto
            -mt-4
            drop-shadow-[0_4px_10px_rgba(0,0,0,0.15)]
          "
        />

        {/* Greeting */}
        <div className="flex-1 flex items-center self-center lg:self-stretch min-w-0">
          {/* Mobile: stacked two-line greeting with dark text */}
          <div className="lg:hidden">
            <p className="text-base sm:text-lg text-text-primary/80 font-medium leading-tight">
              Welcome back
            </p>
            <h1 className="text-xl sm:text-2xl font-extrabold text-text-primary leading-tight tracking-tight mt-0.5 break-words">
              {displayName}
            </h1>
          </div>

          {/* Desktop: single line over the brand gradient, in whichever ink
              that gradient calls for (Aleix, 2026-09-11). */}
          <h1 className="hidden lg:block text-2xl xl:text-[26px] leading-tight font-extrabold text-banner-learner-ink tracking-tight">
            Welcome Back, {firstName}!
          </h1>
        </div>
      </div>
    </motion.section>
  );
};

export default LearnerWelcomeBanner;
